<script setup>
//SettingsWhatsappCortexWoot.vue
import { computed, ref, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useWhatsappAttributes } from 'dashboard/composables/useWhatsappAttributes';
import CustomAttributesEvolutionItem from 'dashboard/routes/dashboard/settings/inbox/components/CustomAttributesEvolutionItem.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'dashboard/components-shadcn/new-york/ui/tabs';

const store = useStore();
const { whatsappAttributes, chatwootAttributes, updateAttribute } = useWhatsappAttributes();

const props = defineProps({
  inbox: {
    type: Object,
    required: true
  }
});

// Estados principais
const isLoading = ref(true);
const activeTab = ref('whatsapp');
const inboxId = ref(props.inbox.id);

// Estados do QR Code
const qrcodeData = ref(null);
const qrcodeError = ref(null);
const isLoadingQR = ref(false);

// Computeds
const selectedInbox = computed(() => store.getters['inboxes/getInboxById'](inboxId.value));
const isApiInbox = computed(() => selectedInbox.value?.channelType === 'Channel::Api');
const isConnected = computed(() => qrcodeData.value?.data?.connected && qrcodeData.value?.data?.status === 'open');

// Função para carregar QR Code
const loadQRCode = async () => {
  if (!isApiInbox.value) return;
  
  isLoadingQR.value = true;
  qrcodeError.value = null;
  
  try {
    const response = await store.dispatch('inboxes/getQRCode', inboxId.value);
    qrcodeData.value = response;
  } catch (error) {
    qrcodeError.value = error.message || 'Erro ao carregar QR Code';
    console.error('Erro ao carregar QR Code:', error);
  } finally {
    isLoadingQR.value = false;
  }
};

// Função para verificar status
const checkStatus = async () => {
  if (!isApiInbox.value) return;
  
  try {
    const response = await store.dispatch('inboxes/checkWhatsappStatus', inboxId.value);
    if (qrcodeData.value) {
      qrcodeData.value.data.status = response.data.status;
      qrcodeData.value.data.connected = response.data.connected;
    }

  } catch (error) {
    console.error('Erro ao verificar status:', error);
  }
};

// Função para desconectar instância (CORRIGIDA)
const disconnectInstance = async () => {
  if (!isApiInbox.value) return;
  
  isLoadingQR.value = true;
  qrcodeError.value = null;
  
  try {
    const response = await store.dispatch('inboxes/disconnectWhatsappInstance', inboxId.value);
    
    if (response.success) {

      
      // Limpa os dados atuais
      qrcodeData.value = null;
      
      // Aguarda um momento para o backend processar a desconexão
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Recarrega o QR Code para obter o novo código
      await loadQRCode();
      
    } else {
      qrcodeError.value = response.error || 'Erro ao desconectar instância';
    }
  } catch (error) {
    qrcodeError.value = error.message || 'Erro ao desconectar instância';
    console.error('Erro ao desconectar instância:', error);
  } finally {
    isLoadingQR.value = false;
  }
};

// Função para processar atributos
const processAttributes = (attributes) => {
  Object.keys(attributes).forEach((key) => {
    const value = attributes[key];
    
    if (key === 'rejectCall') {
      updateAttribute({
        key,
        value,
        rejectionMessage: attributes.msgCall || '',
      });
    } else {
      updateAttribute({ key, value });
    }
  });
};

// Lifecycle
onMounted(() => {
  store.dispatch('inboxes/get').then(() => {
    const additionalAttributes = selectedInbox.value?.additionalAttributes || {};
    processAttributes(additionalAttributes);

    if (isApiInbox.value) {
      loadQRCode();
    }

    isLoading.value = false;
  }).catch(() => {
    isLoading.value = false;
  });
});

// Watchers
watch(selectedInbox, (newInbox) => {
  if (newInbox) {
    const additionalAttributes = newInbox.additionalAttributes || {};
    processAttributes(additionalAttributes);

    if (newInbox.channelType === 'Channel::Api') {
      loadQRCode();
    }
  }
}, { deep: true, immediate: true });
</script>

<template>
  <div v-if="!isLoading" class="mx-8 mt-4">
    <Tabs v-model="activeTab" class="tabs-custom w-full">
      <TabsList class="tabs-list-custom" :class="{ 'three-tabs': isApiInbox }">
        <TabsTrigger value="whatsapp" class="tabs-trigger-custom">
          Configurações do WhatsApp
        </TabsTrigger>
        <TabsTrigger value="cortexwoot" class="tabs-trigger-custom">
          Configurações do CortexWoot
        </TabsTrigger>
        <TabsTrigger v-if="isApiInbox" value="qrcode" class="tabs-trigger-custom">
          QR Code
        </TabsTrigger>
      </TabsList>
      
      <TabsContent value="whatsapp" class="tabs-content-custom">
        <div class="attributes-container">
          <CustomAttributesEvolutionItem
            v-for="attribute in whatsappAttributes"
            :key="attribute.id"
            :attribute="attribute"
            @update-attribute="updateAttribute"
          />
        </div>
      </TabsContent>

      <TabsContent value="cortexwoot" class="tabs-content-custom">
        <div class="attributes-container">
          <CustomAttributesEvolutionItem
            v-for="attribute in chatwootAttributes"
            :key="attribute.id"
            :attribute="attribute"
            @update-attribute="updateAttribute"
          />
        </div>
      </TabsContent>

      <TabsContent v-if="isApiInbox" value="qrcode" class="tabs-content-custom">
        <div class="qr-code-section">
          <!-- Header -->
          <div class="text-center mb-6">
            <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
              Conectar WhatsApp
            </h3>
            <p class="text-sm text-n-slate-11">
              {{ isConnected ? 'Sua instância está conectada' : 'Escaneie o QR Code com seu WhatsApp' }}
            </p>
          </div>

          <!-- Loading -->
          <div v-if="isLoadingQR" class="flex flex-col justify-center items-center p-8">
            <Spinner :size="32" class="text-woot-600 mb-4" />
            <div class="text-n-slate-11">Carregando...</div>
          </div>
          
          <!-- Error -->
          <div v-else-if="qrcodeError" class="p-4 bg-n-ruby-2 border border-n-ruby-6 rounded-lg text-center mb-6">
            <div class="text-n-ruby-11 mb-4">{{ qrcodeError }}</div>
            <NextButton outline slate @click="loadQRCode">
              Tentar Novamente
            </NextButton>
          </div>
          
          <!-- Content -->
          <div v-else-if="qrcodeData" class="text-center">
            
            <!-- Connected State -->
            <div v-if="isConnected" class="mb-6">
              <div class="w-16 h-16 mx-auto bg-n-teal-2 border border-n-teal-6 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-n-teal-11" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h4 class="text-lg font-semibold text-n-slate-12 mb-4">WhatsApp Conectado</h4>
              
              <div class="flex justify-center gap-3 mb-6">
                <NextButton outline slate @click="checkStatus">Verificar Status</NextButton>
                <NextButton outline ruby @click="disconnectInstance">Desconectar</NextButton>
              </div>
            </div>

            <!-- Disconnected State -->
            <div v-else class="mb-6">
              <div class="qr-wrapper mb-6">
                <img 
                  v-if="qrcodeData.data.qrcode_base64"
                  :src="qrcodeData.data.qrcode_base64" 
                  alt="QR Code WhatsApp"
                  class="qr-image"
                />
                <div v-else class="qr-placeholder">
                  <div class="text-n-slate-9 mb-2">QR Code não disponível</div>
                  <div class="text-xs text-n-slate-11">Verifique o status da instância</div>
                </div>
              </div>
              
              <div class="flex justify-center gap-3 mb-6">
                <NextButton outline slate @click="loadQRCode">Atualizar QR</NextButton>
                <NextButton outline iris @click="checkStatus">Verificar Status</NextButton>
              </div>
            </div>
            
            <!-- Status Info -->
            <div class="status-info p-4 bg-n-slate-2 rounded-lg border border-n-slate-6">
              <div class="text-sm text-n-slate-11 space-y-2">
                <div class="flex justify-center items-center gap-2">
                  <span class="font-medium">Status:</span>
                  <span 
                    class="px-2 py-1 rounded-full text-xs font-medium"
                    :class="{
                      'bg-n-teal-2 text-n-teal-11 border border-n-teal-6': qrcodeData.data.status === 'open',
                      'bg-n-amber-2 text-n-amber-11 border border-n-amber-6': qrcodeData.data.status === 'connecting', 
                      'bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-6': qrcodeData.data.status === 'close'
                    }"
                  >
                    {{ qrcodeData.data.status }}
                  </span>
                </div>
                <div class="flex justify-center items-center gap-2">
                  <span class="font-medium">Conectado:</span>
                  <span 
                    class="px-2 py-1 rounded-full text-xs font-medium"
                    :class="qrcodeData.data.connected 
                      ? 'bg-n-teal-2 text-n-teal-11 border border-n-teal-6' 
                      : 'bg-n-ruby-2 text-n-ruby-11 border border-n-ruby-6'"
                  >
                    {{ qrcodeData.data.connected ? 'Sim' : 'Não' }}
                  </span>
                </div>
                <div v-if="qrcodeData.data.pairing_code" class="flex justify-center items-center gap-2">
                  <span class="font-medium">Código:</span>
                  <code class="px-2 py-1 bg-n-slate-3 rounded text-xs font-mono">
                    {{ qrcodeData.data.pairing_code }}
                  </code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  </div>
  
  <div v-else class="mx-8 mt-4">
    <div class="flex flex-col justify-center items-center p-8">
      <Spinner :size="40" class="text-woot-600 mb-4" />
      <div class="text-n-slate-11">Carregando...</div>
    </div>
  </div>
</template>

<style scoped>
.attributes-container {
  padding: 0;
}

.qr-code-section {
  max-width: 500px;
  margin: 0 auto;
}

.qr-wrapper {
  display: inline-block;
  padding: 1rem;
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.qr-image {
  width: 280px;
  height: 280px;
  display: block;
  border-radius: 0.5rem;
}

.qr-placeholder {
  width: 280px;
  height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed rgb(var(--slate-6));
  border-radius: 0.5rem;
  background: rgb(var(--slate-2));
}

/* Tabs */
:deep(.tabs-list-custom) {
  @apply bg-woot-25 dark:bg-slate-800;
  @apply border border-slate-200 dark:border-slate-700 rounded-lg p-1;
  display: grid;
  grid-template-columns: 1fr 1fr;
}

:deep(.tabs-list-custom.three-tabs) {
  grid-template-columns: 1fr 1fr 1fr;
}

:deep(.tabs-trigger-custom) {
  @apply flex items-center justify-center px-3 py-2 text-sm font-medium;
  @apply rounded-md transition-all duration-200;
  @apply text-slate-600 dark:text-slate-400;
  @apply hover:text-slate-900 dark:hover:text-slate-100;
  @apply hover:bg-slate-100 dark:hover:bg-slate-700;
}

:deep(.tabs-trigger-custom[data-state="active"]) {
  @apply bg-white dark:bg-slate-900 text-woot-600 dark:text-woot-400;
  @apply border border-slate-200 dark:border-slate-600;
  @apply shadow-sm;
}

:deep(.tabs-content-custom) {
  @apply mt-4 p-6 bg-white dark:bg-slate-900;
  @apply border border-slate-200 dark:border-slate-700 rounded-lg;
  min-height: 360px;
}

:deep(.tabs-trigger-custom:focus-visible) {
  @apply outline-2 outline-offset-2 outline-woot-500;
}
</style>